import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, Tag, ArrowLeft, BookOpen, Share2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const API = '/api';

export default function BlogPost() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/blogs/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setBlog(d); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  useEffect(() => {
    if (!blog) return;
    fetch(`${API}/blogs?limit=3&category=${blog.category || ''}`)
      .then(r => r.json())
      .then(d => setRelated((d.blogs || []).filter(b => b.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [blog, slug]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: blog.title, url }); } catch (_) {}
    } else {
      navigator.clipboard.writeText(url).then(() => toast.success('Link copied!'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-16 animate-pulse">
          <div className="h-6 bg-slate-100 rounded w-1/3 mb-4" />
          <div className="h-10 bg-slate-100 rounded w-3/4 mb-6" />
          <div className="h-72 bg-slate-100 rounded-2xl mb-8" />
          {[1,2,3,4,5].map(i => <div key={i} className="h-4 bg-slate-100 rounded mb-3 w-full" />)}
        </div>
      </div>
    );
  }

  if (notFound || !blog) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <BookOpen className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Post Not Found</h1>
          <p className="text-slate-500 mb-6">This blog post may have been removed or the URL is incorrect.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-blue-800 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const date = new Date(blog.published_at || blog.created_at).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric'
  });

  // Render content: double newlines = paragraphs, single = line break
  const paragraphs = blog.content.split(/\n{2,}/).filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${blog.title.length > 52 ? blog.title.slice(0, 50) + '…' : blog.title} | Rent Mitra`}</title>
        <meta name="description" content={blog.excerpt} />
        <link rel="canonical" href={`${window.location.origin}/blog/${slug}`} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${window.location.origin}/blog/${slug}`} />
        <meta property="og:site_name" content="Rent Mitra" />
        <meta property="og:locale" content="en_IN" />
        {blog.cover_image ? <meta property="og:image" content={blog.cover_image} /> : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.excerpt} />
        {blog.cover_image ? <meta name="twitter:image" content={blog.cover_image} /> : null}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": blog.title,
          "description": blog.excerpt,
          "image": blog.cover_image || undefined,
          "author": {
            "@type": "Person",
            "name": blog.author_name || "Rent Mitra Team"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Rent Mitra",
            "url": window.location.origin
          },
          "datePublished": blog.published_at || blog.created_at,
          "url": `${window.location.origin}/blog/${slug}`,
          "mainEntityOfPage": `${window.location.origin}/blog/${slug}`
        })}</script>
      </Helmet>
      <Navbar />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/blog" className="hover:text-blue-700 transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Blog
          </Link>
          <span>/</span>
          <span className="text-slate-900 truncate">{blog.title}</span>
        </div>

        {/* Category */}
        {blog.category && (
          <Link to={`/blog?category=${blog.category}`}
            className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-4 hover:bg-blue-100 transition-colors">
            <Tag className="w-3 h-3" />{blog.category.replace('-', ' ')}
          </Link>
        )}

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-5"
          style={{ fontFamily: 'Outfit, sans-serif' }}>
          {blog.title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-slate-500 text-sm mb-8 pb-6 border-b border-slate-100">
          <div className="flex items-center gap-1.5">
            <div className="w-7 h-7 rounded-full bg-blue-700 flex items-center justify-center text-white text-xs font-bold">
              {blog.author_name?.[0]?.toUpperCase() || 'R'}
            </div>
            <span className="font-medium text-slate-700">{blog.author_name || 'Rent Mitra Team'}</span>
          </div>
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{date}</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{blog.read_time || 1} min read</span>
          <button onClick={handleShare} className="ml-auto flex items-center gap-1.5 text-slate-400 hover:text-blue-700 transition-colors text-xs font-semibold">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>

        {/* Cover Image */}
        {blog.cover_image && (
          <div className="rounded-2xl overflow-hidden mb-10 shadow-sm">
            <img src={blog.cover_image} alt={blog.title} className="w-full max-h-96 object-cover" />
          </div>
        )}

        {/* Content */}
        <div className="prose prose-slate prose-lg max-w-none">
          {paragraphs.map((para, idx) => (
            <p key={idx} className="text-slate-700 leading-relaxed mb-5 text-base sm:text-lg whitespace-pre-line">
              {para}
            </p>
          ))}
        </div>

        {/* Tags */}
        {blog.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-slate-100">
            {blog.tags.map(tag => (
              <span key={tag} className="bg-slate-100 text-slate-600 text-xs font-semibold px-3 py-1 rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Author Card */}
        <div className="mt-10 p-6 bg-blue-50 rounded-2xl flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
            {blog.author_name?.[0]?.toUpperCase() || 'R'}
          </div>
          <div>
            <p className="font-bold text-slate-900">{blog.author_name || 'Rent Mitra Team'}</p>
            <p className="text-slate-500 text-sm mt-0.5">Founder & Real Estate Expert at Rent Mitra. Helping thousands find their perfect home across India.</p>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {related.length > 0 && (
        <div className="bg-slate-50 border-t border-slate-100 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              More Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {related.map(b => {
                const rdate = new Date(b.published_at || b.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                return (
                  <Link key={b.blog_id} to={`/blog/${b.slug}`}
                    className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                    <div className="h-36 bg-slate-100 overflow-hidden">
                      {b.cover_image
                        ? <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        : <div className="w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center"><BookOpen className="w-8 h-8 text-blue-300" /></div>
                      }
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-slate-900 text-sm line-clamp-2 mb-1 group-hover:text-blue-700 transition-colors">{b.title}</h4>
                      <span className="text-slate-400 text-xs flex items-center gap-1"><Calendar className="w-3 h-3" />{rdate}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
